<script setup lang="ts">
import { onBeforeMount } from "vue";
import FileDTO = App.Data.FileDTO;
import { useFileFinder } from "@/ui/components/FileBrowser/useFileFinder";
import { FileBrowserAction } from "@/ui/components/FileBrowser/types";
import { toastApiResult } from "@/toaster";
import { useApiClient } from "@/api/client";
import { useBaseStore } from "@/stores/base";
import StoredDocumentDO = App.Data.StoredDocumentDO;
import FileExplorer from "@/ui/components/FileBrowser/FileExplorer.vue";

const props = defineProps({
	fetchDocuments: Function,
	allowCreateNew: Boolean,
});

const resourceTreeCategory = "requested_documents";
const isDialogVisible = defineModel<boolean>();

const emit = defineEmits(["file-selected", "open-explorer"]);

const openStoredDocument = async (file: FileDTO) => {
	if (file) {
		emit("file-selected", file);
		closeDialog();
	}
};

const closeDialog = () => {
	isDialogVisible.value = false;
};

const createAndOpenNewConfiguration = async (file: FileDTO) => {
	const response = await toastApiResult(
		useApiClient("dashboard").createEmptyStoredDocument(
			file as StoredDocumentDO
		)
	);
	if (response.isOk()) {
		await props.fetchDocuments();

		emit("file-selected", file);
		closeDialog();
	}
};

useFileFinder({
	openFile: openStoredDocument,
	createFile: createAndOpenNewConfiguration,
	closeAction: closeDialog,
});

const actions: FileBrowserAction[] = [
	"close",
	"open-file",
	"new-folder",
	"update-folder",
	"delete-folder",
];

const openExplorer = () => {
	emit("open-explorer");
	isDialogVisible.value = false;
};

onBeforeMount(() => {
	if (
		useBaseStore().currentUser?.derivedPermissions.includes(
			"can_manage_explorer"
		) &&
		props.allowCreateNew
	) {
		actions.push("new-file-top");
	}
});
</script>

<template>
	<v-dialog v-model="isDialogVisible" scrim="#000000" overlay-color="black">
		<v-card>
			<v-toolbar>
				<template v-slot:prepend>
					<v-icon start class="ml-3 mr-5">mdi-file-chart-outline</v-icon>
					<v-toolbar-title class="pt-1"> Configurations </v-toolbar-title>
				</template>
			</v-toolbar>
			<v-divider />
			<file-explorer
				:resource-tree-category="resourceTreeCategory"
				:actions="actions"
				:create-new-opens-explorer="true"
				@open-explorer="openExplorer"
			/>
		</v-card>
	</v-dialog>
</template>

<style lang="scss" scoped></style>
